import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { HardDrive, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const Champ = ({ id, label, children }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label>{children}</div>
);

export default function Bienvenue() {
  const { user, etat, ouvrirSession, rafraichirEtat } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ nom: "", email: "", password: "", confirmation: "" });
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);
  if (etat === null) return null;
  if (!etat.local || etat.compte) return <Navigate to={user ? "/configuration" : "/login"} replace />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const creer = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirmation) { setErreur("Les deux mots de passe ne sont pas identiques"); return; }
    setErreur(""); setEnvoi(true);
    try {
      const { data } = await api.post("/local/compte", { nom: f.nom, email: f.email, password: f.password });
      ouvrirSession(data);
      await rafraichirEtat();
      navigate("/configuration");
    } catch (err) { setErreur(errMsg(err)); } finally { setEnvoi(false); }
  };
  return (
    <div className="min-h-screen bg-slate-950 px-4 py-12 text-white" data-testid="bienvenue-page">
      <div className="mx-auto max-w-5xl lg:grid lg:grid-cols-[1fr_420px] lg:gap-16">
        <div className="rise">
          <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-400">Premier lancement</p>
          <h1 className="mt-6 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">Bienvenue.<br /><span className="text-emerald-400">Créons votre espace.</span></h1>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400">Commencez par créer le compte du propriétaire. Vous configurerez ensuite votre entreprise, vos immeubles, vos logements et votre plan comptable.</p>
          <p className="mt-8 flex max-w-md items-start gap-3 text-sm text-slate-300"><HardDrive className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />Tout est enregistré sur cet ordinateur. Aucune donnée n'est envoyée sur Internet.</p>
        </div>
        <form onSubmit={creer} className="mt-10 space-y-4 rounded-2xl bg-white p-7 text-slate-900 shadow-2xl lg:mt-0" data-testid="bienvenue-form">
          <h2 className="text-xl font-bold tracking-tight">Compte du propriétaire</h2>
          <Champ id="nom" label="Votre nom"><Input id="nom" value={f.nom} onChange={set("nom")} maxLength={120} data-testid="bienvenue-nom" /></Champ>
          <Champ id="email" label="Courriel"><Input id="email" type="email" required value={f.email} onChange={set("email")} data-testid="bienvenue-email" /></Champ>
          <Champ id="password" label="Mot de passe (10 caractères, une lettre et un chiffre)">
            <PasswordInput id="password" required minLength={10} autoComplete="new-password" value={f.password} onChange={set("password")} testId="bienvenue-password" />
          </Champ>
          <Champ id="confirmation" label="Confirmer le mot de passe">
            <PasswordInput id="confirmation" required autoComplete="new-password" value={f.confirmation} onChange={set("confirmation")} testId="bienvenue-confirmation" />
          </Champ>
          {erreur && <p className="text-sm text-red-600" data-testid="bienvenue-erreur">{erreur}</p>}
          <Button type="submit" disabled={envoi} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="bienvenue-creer">
            {envoi && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Créer mon compte
          </Button>
          <p className="text-xs text-slate-500">Notez bien ce mot de passe : il protège l'accès à vos données sur cet ordinateur.</p>
        </form>
      </div>
    </div>
  );
}
